import { gql, useLazyQuery } from "@apollo/client";
import { isAfter } from "date-fns";
import {
  CandidateAvailabilityFragment,
  GuidePostForCandidateAvailabilityCalendar,
  GuidePostForCandidateAvailabilityCalendarVariables,
} from "generated/schemaTypes";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";

const CANDIDATE_AVAILABILITY_CALENDAR_EVENT_FRAGMENT = gql`
  fragment CandidateAvailabilityCalendarEventFragment on CalendarEvent {
    id
    title
    start
    end
    allDay
    createdBy {
      id
    }
  }
`;

const CANDIDATE_AVAILABILITY_FRAGMENT = gql`
  fragment CandidateAvailabilityFragment on CandidateAvailability {
    id
    title
    overlayCalendars
    calendarEvents {
      ...CandidateAvailabilityCalendarEventFragment
    }
    availabilityRequest {
      id
      title
      description
      calendarEvents {
        ...CandidateAvailabilityCalendarEventFragment
      }
      candidateAvailabilities {
        id
        createdAt
      }
    }
  }
  ${CANDIDATE_AVAILABILITY_CALENDAR_EVENT_FRAGMENT}
`;

const GUIDE_POST_FOR_CANDIDATE_AVAILABILITY_CALENDAR = gql`
  query GuidePostForCandidateAvailabilityCalendar($guidePostId: String!) {
    guidePostById(id: $guidePostId) {
      id
      candidateAvailabilities {
        ...CandidateAvailabilityFragment
      }
    }
  }
  ${CANDIDATE_AVAILABILITY_FRAGMENT}
`;

export type ShareAvailabilityRichBlockContextValue = {
  guidePostId?: string;
  candidateAvailabilities: CandidateAvailabilityFragment[];
  getCandidateAvailabilityById: (
    id: string
  ) => CandidateAvailabilityFragment | undefined;
  loading: boolean;
  fetchRequiredData: () => void;
  onClickChange?: () => void;
  isMostRecent: (id: string) => boolean;
  isRevision?: boolean;
};
const ShareAvailabilityRichBlockContext = createContext<
  ShareAvailabilityRichBlockContextValue | undefined
>(undefined);

export function useShareAvailability() {
  const contextValue = useContext(ShareAvailabilityRichBlockContext);

  useEffect(() => {
    // Fetch required data only when the context is requested which will only happen inside the relevant rich block components
    contextValue?.fetchRequiredData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!contextValue) {
    throw new Error(
      "useShareAvailability can only be called inside a ShareAvailabilityRichBlockProvider"
    );
  }

  return contextValue;
}

export function ShareAvailabilityRichBlockProvider({
  children,
  guidePostId,
  onClickChange,
  isRevision,
}: {
  children: ReactNode;
  guidePostId?: string;
  onClickChange?: () => void;
  isRevision?: boolean;
}) {
  const [
    getGuidePostForCandidateAvailabilityCalendar,
    { called, data, loading },
  ] = useLazyQuery<
    GuidePostForCandidateAvailabilityCalendar,
    GuidePostForCandidateAvailabilityCalendarVariables
  >(GUIDE_POST_FOR_CANDIDATE_AVAILABILITY_CALENDAR);

  const fetchRequiredData = useCallback(() => {
    if (!called && guidePostId) {
      getGuidePostForCandidateAvailabilityCalendar({
        variables: {
          guidePostId: guidePostId ?? "",
        },
      });
    }
  }, [called, guidePostId, getGuidePostForCandidateAvailabilityCalendar]);

  const candidateAvailabilities = useMemo(
    () => data?.guidePostById?.candidateAvailabilities ?? [],
    [data?.guidePostById?.candidateAvailabilities]
  );

  const getCandidateAvailabilityById = useCallback(
    (id: string) =>
      candidateAvailabilities.find(
        (candidateAvailability) => candidateAvailability.id === id
      ),
    [candidateAvailabilities]
  );

  const isMostRecent = useCallback(
    (id: string) => {
      const availability = getCandidateAvailabilityById(id);
      if (!availability?.availabilityRequest?.candidateAvailabilities) {
        return true;
      }
      const parentCandidateAvailabilities =
        availability.availabilityRequest?.candidateAvailabilities;
      const mostRecentAvailability = parentCandidateAvailabilities.reduce(
        (mostRecent, current) =>
          isAfter(new Date(current.createdAt), new Date(mostRecent.createdAt))
            ? current
            : mostRecent,
        parentCandidateAvailabilities[0]
      );
      return mostRecentAvailability?.id === availability.id;
    },
    [getCandidateAvailabilityById]
  );

  return (
    <ShareAvailabilityRichBlockContext.Provider
      value={{
        guidePostId,
        candidateAvailabilities,
        getCandidateAvailabilityById,
        loading,
        fetchRequiredData,
        onClickChange,
        isMostRecent,
        isRevision,
      }}
    >
      {children}
    </ShareAvailabilityRichBlockContext.Provider>
  );
}

ShareAvailabilityRichBlockProvider.fragments = {
  candidateAvailability: CANDIDATE_AVAILABILITY_FRAGMENT,
};
